import { useState, type FormEvent } from 'react';
import { api, type Me } from '../api';
import { BrandMark, ErrorNote, Field, Spinner } from '../components/ui';

/**
 * Sign in. Password first; when one-time codes are switched on (and a delivery
 * webhook is configured), the code step follows. A code is never asked for
 * before the password has passed.
 *
 * Forgot password: a code emailed to the address (through AgeDash), which works
 * only in this browser. The screen says the same thing whether or not the
 * address has an account.
 */
export default function SignIn({ appName, onSignedIn }: { appName: string; onSignedIn: (me: Me) => void }) {
  const [stage, setStage] = useState<'password' | 'otp' | 'forgot' | 'reset'>('password');
  const [next, setNext] = useState('');
  const [next2, setNext2] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function submitPassword(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ status: string; user?: Me }>('/api/auth/signin', { email, password });
      if (r.status === 'otp_required') {
        setStage('otp');
        setNotice('We sent a six-digit code to you.');
      } else if (r.user) onSignedIn(r.user);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function submitOtp(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ user: Me }>('/api/auth/otp/verify', { code });
      onSignedIn(r.user);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  function back() {
    setStage('password');
    setCode('');
    setNext('');
    setNext2('');
    setNotice(null);
    setError(null);
  }

  async function startReset(e?: FormEvent) {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/auth/forgot', { email });
      setCode('');
      setNotice(`If ${email} has an account here, a six-digit code is on its way to it.`);
      setStage('reset');
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function submitReset(e: FormEvent) {
    e.preventDefault();
    if (next !== next2) {
      setError(new Error('The two new passwords are not the same.'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/auth/reset', { code, password: next });
      setPassword('');
      back();
      setNotice('Your password is changed and every device was signed out. Sign in with the new one.');
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    setError(null);
    try {
      const r = await api.post<{ message: string }>('/api/auth/otp/resend');
      setNotice(r.message);
    } catch (err) {
      setError(err);
    }
  }

  return (
    <div className="auth">
      <div className="auth-box">
        <div className="brand">
          <BrandMark />
          {appName}
        </div>
        <div className="auth-card">
          {stage === 'password' ? (
            <>
              <h2 style={{ margin: '0 0 4px', fontSize: 19 }}>Sign in to the voice demo</h2>
              <p className="sub" style={{ marginBottom: 18 }}>Use the demo login you were given.</p>
              {notice ? <div className="note success" role="status">{notice}</div> : null}
              <ErrorNote error={error} />
              <form onSubmit={submitPassword}>
                <Field label="Email" htmlFor="email">
                  <input id="email" type="email" autoComplete="username" autoFocus required value={email} onChange={(e) => setEmail(e.target.value)} />
                </Field>
                <Field label="Password" htmlFor="password">
                  <input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
                </Field>
                <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '-6px 0 12px' }}>
                  <button className="btn sm ghost" type="button" onClick={() => { setStage('forgot'); setNotice(null); setError(null); }}>
                    Forgot password?
                  </button>
                </div>
                <button className="btn primary" style={{ width: '100%' }} disabled={busy}>
                  {busy ? <Spinner label="Signing in" /> : null} Sign in
                </button>
              </form>
            </>
          ) : stage === 'forgot' ? (
            <>
              <h2 style={{ margin: '0 0 4px', fontSize: 19 }}>Reset your password</h2>
              <p className="sub" style={{ marginBottom: 18 }}>Enter your email. We will send a six-digit code to it.</p>
              <ErrorNote error={error} />
              <form onSubmit={startReset}>
                <Field label="Email" htmlFor="reset-email">
                  <input id="reset-email" type="email" autoComplete="username" autoFocus required value={email} onChange={(e) => setEmail(e.target.value)} />
                </Field>
                <button className="btn primary" style={{ width: '100%' }} disabled={busy}>
                  {busy ? <Spinner label="Sending" /> : null} Send me a code
                </button>
              </form>
              <div className="toolbar" style={{ marginTop: 14, marginBottom: 0, justifyContent: 'flex-end' }}>
                <button className="btn sm" type="button" onClick={back}>Back to sign in</button>
              </div>
            </>
          ) : stage === 'reset' ? (
            <>
              <h2 style={{ margin: '0 0 4px', fontSize: 19 }}>Choose a new password</h2>
              <p className="sub" style={{ marginBottom: 18 }}>Type the code from the email, then a new password.</p>
              {notice ? <div className="note" role="status">{notice}</div> : null}
              <ErrorNote error={error} />
              <form onSubmit={submitReset}>
                <Field label="Six-digit code" htmlFor="reset-code">
                  <input
                    id="reset-code" inputMode="numeric" pattern="\d{6}" maxLength={6} autoFocus required autoComplete="one-time-code"
                    value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    style={{ fontFamily: 'var(--f-mono)', fontSize: 22, letterSpacing: '0.3em', textAlign: 'center' }}
                  />
                </Field>
                <Field label="New password (at least 10 characters)" htmlFor="reset-next">
                  <input id="reset-next" type="password" autoComplete="new-password" required minLength={10} value={next} onChange={(e) => setNext(e.target.value)} />
                </Field>
                <Field label="New password again" htmlFor="reset-next2">
                  <input id="reset-next2" type="password" autoComplete="new-password" required minLength={10} value={next2} onChange={(e) => setNext2(e.target.value)} />
                </Field>
                <button className="btn primary" style={{ width: '100%' }} disabled={busy || code.length !== 6 || next.length < 10}>
                  {busy ? <Spinner label="Saving" /> : null} Change password
                </button>
              </form>
              <div className="toolbar" style={{ marginTop: 14, marginBottom: 0, justifyContent: 'space-between' }}>
                <button className="btn sm" type="button" disabled={busy} onClick={() => startReset()}>Send a new code</button>
                <button className="btn sm" type="button" onClick={back}>Back to sign in</button>
              </div>
            </>
          ) : (
            <>
              <h2 style={{ margin: '0 0 4px', fontSize: 19 }}>Enter your code</h2>
              <p className="sub" style={{ marginBottom: 18 }}>It expires in a few minutes.</p>
              {notice ? <div className="note" role="status">{notice}</div> : null}
              <ErrorNote error={error} />
              <form onSubmit={submitOtp}>
                <Field label="Six-digit code" htmlFor="code">
                  <input
                    id="code" inputMode="numeric" pattern="\d{6}" maxLength={6} autoFocus required autoComplete="one-time-code"
                    value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    style={{ fontFamily: 'var(--f-mono)', fontSize: 22, letterSpacing: '0.3em', textAlign: 'center' }}
                  />
                </Field>
                <button className="btn primary" style={{ width: '100%' }} disabled={busy || code.length !== 6}>
                  {busy ? <Spinner label="Verifying" /> : null} Verify and sign in
                </button>
              </form>
              <div className="toolbar" style={{ marginTop: 14, marginBottom: 0, justifyContent: 'space-between' }}>
                <button className="btn sm" onClick={resend}>Send another code</button>
                <button className="btn sm" onClick={back}>Back</button>
              </div>
            </>
          )}
        </div>
        <p className="small muted" style={{ textAlign: 'center', marginTop: 16 }}>
          Voice demo · speech by Deepgram · voices by Rumik and ElevenLabs
        </p>
      </div>
    </div>
  );
}
